import { Button, Icon, Label, Textarea, cn, useCopy } from "@sagot/ui";

/** Transparent hit-area expansion: 32px icon buttons / 22px chips reach the 44px end-user touch target. */
const TOUCH_ICON = "relative after:absolute after:-inset-1.5 after:content-['']";
const TOUCH_CHIP = "relative after:absolute after:-inset-x-0.5 after:-inset-y-[11px] after:content-['']";
import { useId, useState } from "react";
import { FEEDBACK_NOTE_MAX_CHARS, FEEDBACK_REASONS } from "../constants";
import type { FeedbackInput, FeedbackReason, FeedbackVote } from "../types";

export interface FeedbackBarLabels {
  helpful: string;
  notHelpful: string;
  copy: string;
  copied: string;
  gap: string;
  reasonsTitle: string;
  note: string;
  noteHint: string;
  send: string;
  thanks: string;
}

const DEFAULT_LABELS: FeedbackBarLabels = {
  helpful: "Helpful",
  notHelpful: "Not helpful",
  copy: "Copy",
  copied: "Copied",
  gap: "Logged as knowledge gap",
  reasonsTitle: "What was wrong with this answer?",
  note: "Tell us more (optional)",
  noteHint: "Sent to the team, not public",
  send: "Send feedback",
  thanks: "Thanks — the team will review this answer.",
};

export interface FeedbackBarProps {
  /** Plain-text answer for Copy. */
  copyText: string;
  onFeedback: (input: FeedbackInput) => void | Promise<void>;
  gap?: boolean;
  /** `quick`: a reason click sends immediately (web/mobile). `form`: reason + optional note + Send (widget). */
  variant?: "quick" | "form";
  labels?: Partial<FeedbackBarLabels>;
}

const VOTE_ON = "text-primary-ink bg-primary-soft";

function ReasonForm({ variant, labels, onSubmit }: { variant: "quick" | "form"; labels: FeedbackBarLabels; onSubmit: (reason: FeedbackReason, note?: string) => void }) {
  const id = useId();
  const [reason, setReason] = useState<FeedbackReason | null>(null);
  const [note, setNote] = useState("");
  return (
    <div role="group" aria-labelledby={`${id}-title`} className="mt-1 space-y-2">
      <p id={`${id}-title`} className={cn("text-[11px] font-semibold text-fg-muted", variant === "quick" && "sr-only")}>
        {labels.reasonsTitle}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {FEEDBACK_REASONS.map((r) => (
          <button
            key={r.value}
            type="button"
            aria-pressed={reason === r.value}
            onClick={() => {
              if (variant === "quick") onSubmit(r.value);
              else setReason(r.value);
            }}
            className={cn(TOUCH_CHIP, "cursor-pointer rounded-pill border border-line px-2.5 py-1 text-[11px] font-semibold text-fg-muted hover:border-primary aria-pressed:border-primary aria-pressed:bg-primary-soft aria-pressed:text-primary-ink")}
          >
            {r.label}
          </button>
        ))}
      </div>
      {variant === "form" ? (
        <>
          <div>
            <Label htmlFor={`${id}-note`} className="sr-only">
              {labels.note}
            </Label>
            <Textarea
              id={`${id}-note`}
              rows={2}
              maxLength={FEEDBACK_NOTE_MAX_CHARS}
              placeholder={labels.note}
              aria-describedby={`${id}-hint`}
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
              className="text-xs"
            />
            <p id={`${id}-hint`} className="mt-1 text-[11px] text-fg-subtle">
              {labels.noteHint}
            </p>
          </div>
          <Button
            size="sm"
            disabled={!reason}
            onClick={() => {
              if (reason) onSubmit(reason, note.trim() || undefined);
            }}
          >
            {labels.send}
          </Button>
        </>
      ) : null}
    </div>
  );
}

/** 👍 / 👎 / Copy under an answer; 👎 opens the reasons (exact labels). */
export function FeedbackBar({ copyText, onFeedback, gap, variant = "form", labels }: FeedbackBarProps) {
  const l = { ...DEFAULT_LABELS, ...labels };
  const [vote, setVote] = useState<FeedbackVote | null>(null);
  const [sent, setSent] = useState(false);
  const { copied, copy } = useCopy();

  const choose = (next: FeedbackVote) => {
    setVote(next);
    if (next === "up") void onFeedback({ vote: "up" });
  };
  const submitReason = (reason: FeedbackReason, note?: string) => {
    setSent(true);
    void onFeedback({ vote: "down", reason, note });
  };

  return (
    <div>
      <div className="mt-1.5 flex items-center gap-0.5 text-fg-subtle">
        <Button variant="ghost" size="sm" iconOnly aria-label={l.helpful} aria-pressed={vote === "up"} className={cn(TOUCH_ICON, vote === "up" && VOTE_ON)} onClick={() => { choose("up"); }}>
          <Icon name="thumbs-up" />
        </Button>
        <Button variant="ghost" size="sm" iconOnly aria-label={l.notHelpful} aria-pressed={vote === "down"} className={cn(TOUCH_ICON, vote === "down" && VOTE_ON)} onClick={() => { choose("down"); }}>
          <Icon name="thumbs-down" />
        </Button>
        <Button variant="ghost" size="sm" iconOnly className={TOUCH_ICON} aria-label={copied ? l.copied : l.copy} onClick={() => { void copy(copyText); }}>
          <Icon name={copied ? "check" : "copy"} />
        </Button>
        {gap ? (
          <span className="badge badge-warning ml-1">
            <Icon name="help-circle" className="size-3" />
            {l.gap}
          </span>
        ) : null}
      </div>
      {vote === "down" && !sent ? <ReasonForm variant={variant} labels={l} onSubmit={submitReason} /> : null}
      {sent ? (
        <p role="status" className="mt-1 text-[11px] text-fg-subtle">
          {l.thanks}
        </p>
      ) : null}
    </div>
  );
}
